import { useCallback } from 'react';
import { useCreateMutation } from '../../graphql';
import { useCurrentLocation } from '../../location';
import { CREATE_TAG_SCAN } from '../../offline/offlineMutations';
import { useOfflineSubmit } from '../../offline/useOfflineSubmit';
import { newGUID } from '../../utils/newGuid';

export interface TagScanOptions {
  /** The scan attempt's id; a scan sent twice under it is one scan. Minted here when the scan is queued without one. */
  id?: string;
  /** When the tag was scanned. Defaults to now. */
  capturedAt?: Date | number;
  tagCode?: string;
  /**
   * Never send directly: reject with `OutboxUnavailableError` when the outbox
   * cannot take the scan. A direct request made while offline waits in memory
   * and is lost if the app is killed, so a caller that may be offline asks for
   * this and decides what to do instead.
   */
  queueOnly?: boolean;
}

export interface TagScanResult {
  /** True when the scan is durably in the outbox; false when it was sent directly. */
  queued: boolean;
}

export const useTagScan = () => {
  const { mutate } = useCreateMutation({
    modelName: 'tagScan',
    query: CREATE_TAG_SCAN,
  });
  const { submit, canQueue } = useOfflineSubmit();

  const { getLocation } = useCurrentLocation();

  const tagScan = useCallback(
    async (
      tagId: string,
      options: TagScanOptions = {}
    ): Promise<TagScanResult> => {
      const location = await getLocation();

      // A queue-only caller goes to the outbox even when `canQueue` read false
      // at render: the outbox decides at call time, and a caller that waited
      // for it to become ready may not have re-rendered since.
      if (canQueue || options.queueOnly) {
        const accepted = await submit(
          'tagScan',
          {
            id: options.id ?? newGUID(),
            tagId,
            tagCode: options.tagCode,
            capturedAt: options.capturedAt ?? Date.now(),
            location,
          },
          { queueOnly: options.queueOnly }
        );
        return { queued: accepted.rowId !== null };
      }

      // The attempt's id and time go along only when the caller chose them:
      // the request is otherwise the one every caller has always sent.
      const input = {
        tag: { id: tagId },
        location: location,
        ...(options.id ? { id: options.id } : {}),
        ...(options.capturedAt
          ? { createdAt: new Date(options.capturedAt).toISOString() }
          : {}),
      };

      await mutate({ input: input });
      return { queued: false };
    },
    [getLocation, mutate, submit, canQueue]
  );

  return tagScan;
};
